import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import instance from "api/apiClient";
import { apiRoutes } from "configs/apiRoutes";
import { mergeExistingFields } from "helpers/mergeExistingFields";
import sanitizeFileName from "helpers/sanitizeFileName";
import { ApiErrorPayload } from "types/ApiErrorPayload";

export interface PartnerBanner {
  id: number;
  href: string;
  priority: number;
  alt: string;
  image: string;
}

export interface Partner {
  id: number;
  title: string;
  geo: string;
  isActive: boolean;
  items: PartnerBanner[];
};

export type DraftItemBanner = Omit<PartnerBanner, 'image'> & {
  imageHref?: string;
  image?: File;
  isModified: boolean;
  isNew?: boolean;
}

type PartnerDraftItem = Omit<Partner, 'items'> & {
  isModified: boolean;
  items: DraftItemBanner[];
};

export interface PartnersState {
  list: Omit<Partner, 'items'>[];
  isListFetched: boolean;
  isItemFetched: boolean;
  isItemFetching: boolean;
  selectedItems: Partner['id'][];
  processingItemId?: Partner['id'];
  processindBannerId?: PartnerBanner['id'];
  activePage: number;
  totalItems: number;
  totalPages: number;
  draftItem: PartnerDraftItem;
};

interface GetPartnersListPayload {
  limit?: number;
  offset?: number;
  title?: string;
  countries?: string[];
  isActive?: number;
  order?: Partial<Record<keyof Omit<Partner, 'items'>, string>>
};

interface GetPartnersListResponse {
  items: Omit<Partner, 'items'>[];
  totalItems: number;
  totalPages: number;
};

type CreateUpdateDraftItemPayload = Partial<Omit<PartnerDraftItem, 'items'> & {
  items: Pick<DraftItemBanner, 'alt' | 'href' | 'priority' | 'image'>[]
}>

interface PartnersBunchUpdatePayload {
  ids: number[];
  isActive: number;
}

const initialPartner: PartnerDraftItem = {
  id: 0,
  title: '',
  geo: '',
  isActive: false,
  items: [],
  isModified: false
};

const initialState: PartnersState = {
  list: [],
  selectedItems: [],
  isListFetched: false,
  isItemFetched: false,
  isItemFetching: false,
  activePage: 1,
  totalItems: 0,
  totalPages: 0,
  draftItem: initialPartner
};

const createDataForUpdate = (data: CreateUpdateDraftItemPayload) => {
  const formData = new FormData();

  const { id, isModified, ...inputData } = data; 

  Object.keys(inputData).forEach((key) => {
    const k = key as keyof Partner;

    switch (k as keyof Partner) {
      case "isActive": {
        formData.append("isActive", `${data[k] ? 1 : 0}`);
        return;
      }
      case "geo": {
        formData.append("country", `${data[k]}`);
        return;
      }

      case "items": {
        const banners = data[k] as DraftItemBanner[];

        banners.forEach((banner, index) => {
          formData.append(`items[${index}][id]`, `${banner.id}`);
          formData.append(`items[${index}][alt]`, banner.alt ?? "");
          formData.append(`items[${index}][href]`, banner.href ?? "");
          formData.append(
            `items[${index}][priority]`,
            `${banner.priority ?? index + 1}`,
          );

          if (banner.image instanceof File) {
            const safeName = sanitizeFileName(banner.image.name);

            formData.append(
              `items[${index}][image]`,
              new File([banner.image], safeName, { type: banner.image.type }),
            );
          }
        });

        return;
      }

      default:
        formData.append(k, `${data[k]}`);
    }
  });

  return formData;
};

const createDraftItem = (data: Partner) => {
  const { id, title, isActive, items, geo } = data;

  const banners: DraftItemBanner[] = items.map((item) => ({
    id: item.id,
    href: item.href,
    priority: item.priority,
    alt: item.alt,
    imageHref: item.image,
    isModified: false,
  }));

  const draftItem: PartnerDraftItem = {
    id,
    title,
    isActive,
    geo,
    isModified: false,
    items: banners,
  };

  return draftItem;
};

const bunchUpdatePartners = createAsyncThunk(
  "cms-partners/bunch-update",
  async (data: PartnersBunchUpdatePayload) => {
    const result = await instance.put(apiRoutes.partnersBunchUpdate, data);

    return result.data;
  }
);

const getPartnersList = createAsyncThunk(
  "cms-partners/list",
  async (data: GetPartnersListPayload) => {
    try {
      const result = await instance.post(`${apiRoutes.partnersList}`, data);
      return result.data;
    } catch (error) {
      return undefined;
    }
  },
);

const getPartnerItem = createAsyncThunk(
  "cms-partners/item",
  async (id: number) => {
    try {
      const result = await instance.get(`${apiRoutes.partnersItem}/${id}`);
      return result.data as Partner;
    } catch (error) {
      throw error;
    }
  },
);

const deletePartnerItem = createAsyncThunk(
  "cms-partners/delete",
  async (id: number) => {
    try {
      await instance.delete(`${apiRoutes.partnersDelete}/${id}`);
      return true;
    } catch (error) {
      throw error;
    }
  },
);



const createPartnerItem = createAsyncThunk<
  Partner,
  CreateUpdateDraftItemPayload,
  { rejectValue: ApiErrorPayload }
>(
  "cms-partners/create",
  async (data: CreateUpdateDraftItemPayload, { rejectWithValue }) => {
    try {
      const formData = createDataForUpdate(data);

      const result = await instance.post(
        `${apiRoutes.partnersCreate}`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        },
      );

      return result.data as Partner;
    } catch (error: any) {
      const apiError = error?.data?.error;
      if (apiError?.code) {
        return rejectWithValue(apiError);
      }

      return rejectWithValue({
        code: "InternalError",
        fieldErrors: [],
        globalErrors: [{ code: "unknownError" }],
      });
    }
  },
);

const updatePartnerItem = createAsyncThunk<
  Partner,
  CreateUpdateDraftItemPayload,
  { rejectValue: ApiErrorPayload }
>(
  "cms-partners/update",
  async (data: CreateUpdateDraftItemPayload, { rejectWithValue }) => {
    try {
      const formData = createDataForUpdate(data);

      const result = await instance.put(
        `${apiRoutes.partnersUpdate}/${data.id}`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        },
      );

      return result.data as Partner;
    } catch (error: any) {
      const data = error?.data;
      const apiError = data?.error ?? data;

      if (apiError && apiError.code) {
        return rejectWithValue(apiError);
      }

      return rejectWithValue({
        code: "InternalError",
        fieldErrors: [],
        globalErrors: [{ code: "unknownError" }],
      });
    }
  },
);

const deleteBannerItem = createAsyncThunk(
  "cms-partners/delete-banner",
  async ({ partnerId, bannerId }: { partnerId: Partner['id'], bannerId: PartnerBanner['id'] }) => {
    try {
      const result = await instance.delete(`${apiRoutes.partnersDeleteBanner}/${partnerId}`, {
        data: {
          mediaId: bannerId
        }
      });

      return result.data as Partner;
    } catch (error) {
      throw error;
    }
  }
);

const partnersSlice = createSlice({
  name: 'cms-partners',
  initialState,
  reducers: {
    handleSelect: (state, action: PayloadAction<number>) => {
      if (state.selectedItems.includes(action.payload)) {
        state.selectedItems = state.selectedItems.filter(
          (el) => el !== action.payload,
        )
      } else {
        state.selectedItems.push(action.payload);
      }
    },
    handleSelectAll: (state) => {
      state.selectedItems = state.selectedItems.length !== state.list.length
        ? state.list.map((item) => item.id)
        : [];
    },
    handlePaginate: (state, action: PayloadAction<number>) => {
      state.activePage = action.payload;
      state.isListFetched = false;
      state.selectedItems = [];
    },
    handleClearDraftItem: (state) => {
      state.draftItem = { ...initialPartner };
      state.isItemFetched = false;
    },
    handleUpdateDraftItem: (state, action: PayloadAction<Partial<PartnerDraftItem>>) => {
      state.draftItem = {
        ...state.draftItem,
        ...action.payload,
        isModified: true
      };
    },
    handleAddBannerToDraftItem: (state) => {
      const newIndex =
        (state.draftItem.items.length
          ? Math.max(...state.draftItem.items.map((item) => item.id))
          : 0) + 1;

      state.draftItem.items = [
        ...state.draftItem.items,
        {
          id: newIndex,
          href: '',
          priority: newIndex,
          alt: '',
          imageHref: '',
          isModified: false,
          isNew: true,
        }
      ];

    },
    handleDeleteBannerFromDraftItem: (state, action: PayloadAction<DraftItemBanner['id']>) => {
      state.draftItem.items = state.draftItem.items.filter((item) => item.id !== action.payload);
      state.draftItem.isModified = true;
    },
    handleUpdateDraftItemBanner: (state, action: PayloadAction<Partial<DraftItemBanner>>) => {
      const bannerId = action.payload.id ?? null;

      
      if (bannerId === null || !state.draftItem.items.find((item) => item.id === bannerId)) {
        return;
      }

      const updatedBanners = state.draftItem.items.map((item) => {
        if (item.id === bannerId) {
          return {
            ...item,
            ...action.payload,
            isModified: true,
          };
        }

        return item;
      });

      state.draftItem = {
        ...state.draftItem,
        items: updatedBanners,
      };
    }
  },
  extraReducers: (builder) => {
    builder.addCase(getPartnersList.fulfilled, (state, action) => {
      state.isListFetched = true;
      state.list = action.payload?.items ?? [];
      state.totalItems = action.payload?.totalItems ?? 0;
      state.totalPages = action.payload?.totalPages ?? 1;
    });
    builder.addCase(getPartnerItem.fulfilled, (state, action) => {
      state.isItemFetched = true;
      const draftItem = createDraftItem(action.payload);
      state.draftItem = draftItem;

    });
    builder.addCase(deletePartnerItem.pending, (state, action) => {
      state.processingItemId = action.meta.arg;
    });
    builder.addCase(deletePartnerItem.fulfilled, (state) => {
      state.processingItemId = undefined;
      state.isListFetched = false;
    });
    builder.addCase(deletePartnerItem.rejected, (state) => {
      state.processingItemId = undefined;
    });
    builder.addCase(updatePartnerItem.pending, (state, action) => {
      state.isItemFetching = true;
      state.processingItemId = action.meta.arg.id;
    });
    builder.addCase(updatePartnerItem.fulfilled, (state, action) => {
      state.isItemFetching = false;
      state.list = state.list.map((item) =>
        item.id === action.payload.id
          ? mergeExistingFields(item, action.payload)
          : item,
      );
      state.processingItemId = undefined;
      const draftItem = createDraftItem(action.payload);
      state.draftItem = draftItem;
    });
    builder.addCase(updatePartnerItem.rejected, (state) => {
      state.isItemFetching = false;
      state.processingItemId = undefined;
    });
    builder.addCase(createPartnerItem.pending, (state) => {
      state.isItemFetching = true;
    });
    builder.addCase(createPartnerItem.fulfilled, (state) => {
      state.isItemFetching = false;
      state.isListFetched = false;
    });
    builder.addCase(createPartnerItem.rejected, (state) => {
      state.isItemFetching = false;
    });
    builder.addCase(deleteBannerItem.pending, (state, action) => {
      state.processindBannerId = action.meta.arg.bannerId;
    });
    builder.addCase(deleteBannerItem.fulfilled, (state, action ) => {
      state.processindBannerId = undefined;
      state.draftItem.items = state.draftItem.items.filter(
        (item) => item.id !== action.meta.arg.bannerId,
      );
    });
    builder.addCase(deleteBannerItem.rejected, (state) => {
      state.processindBannerId = undefined;
    });
    builder.addCase(bunchUpdatePartners.fulfilled, (state) => {
      state.isListFetched = false;
      state.selectedItems = [];
    });
  }
});

const {
  handleSelect,
  handleSelectAll,
  handlePaginate,
  handleClearDraftItem,
  handleUpdateDraftItem,
  handleUpdateDraftItemBanner,
  handleAddBannerToDraftItem,
  handleDeleteBannerFromDraftItem,
} = partnersSlice.actions;

export {
  partnersSlice,
  handleSelect,
  handleSelectAll,
  handlePaginate,
  handleClearDraftItem,
  handleUpdateDraftItem,
  getPartnersList,
  getPartnerItem,
  updatePartnerItem,
  createPartnerItem,
  deletePartnerItem,
  handleUpdateDraftItemBanner,
  handleAddBannerToDraftItem,
  handleDeleteBannerFromDraftItem,
  deleteBannerItem,
  bunchUpdatePartners,
};
